import { fileExtension } from "./document-preview";

export type ImageSupport = {
  format: string;
  support: "native" | "still-preview" | "attachment";
  note: string;
};

const formats: Record<string, ImageSupport> = {
  png: {
    format: "PNG",
    support: "native",
    note: "Native image with contextual tools and alpha",
  },
  jpg: {
    format: "JPEG",
    support: "native",
    note: "Native image with contextual tools and orientation handling",
  },
  jpeg: {
    format: "JPEG",
    support: "native",
    note: "Native image with contextual tools and orientation handling",
  },
  webp: {
    format: "WebP",
    support: "native",
    note: "Still WebP with contextual tools; animation is not accepted",
  },
  gif: {
    format: "GIF",
    support: "still-preview",
    note: "Static first-frame poster; original animation retained",
  },
  bmp: {
    format: "BMP",
    support: "still-preview",
    note: "Converted still preview; original retained",
  },
  svg: {
    format: "SVG",
    support: "still-preview",
    note: "Safely rasterized still preview; original vector retained",
  },
  avif: {
    format: "AVIF",
    support: "still-preview",
    note: "Browser-decoded still preview when available; original retained",
  },
  heic: {
    format: "HEIC",
    support: "attachment",
    note: "Original download only; private bounded conversion is pending",
  },
  heif: {
    format: "HEIF",
    support: "attachment",
    note: "Original download only; private bounded conversion is pending",
  },
  tif: {
    format: "TIFF",
    support: "attachment",
    note: "Original download only; multi-page conversion is pending",
  },
  tiff: {
    format: "TIFF",
    support: "attachment",
    note: "Original download only; multi-page conversion is pending",
  },
};

export function imageFormatSupport(name: string): ImageSupport | null {
  return formats[fileExtension(name)] ?? null;
}

/** Conservative SVG gate before any browser decoder sees private source bytes. */
export function assertSafeSvgText(text: string) {
  if (new TextEncoder().encode(text).byteLength > 2 * 1024 * 1024)
    throw new Error("SVG previews support files up to 2 MB.");
  if (!/^\s*(?:<\?xml[^>]*>\s*)?<svg\b/i.test(text))
    throw new Error("This is not a valid SVG image.");
  if (/<!DOCTYPE|<!ENTITY/i.test(text))
    throw new Error("SVG document declarations are not supported.");
  if (
    /<\/?(?:script|foreignObject|iframe|object|embed|audio|video|image)\b/i.test(
      text,
    )
  )
    throw new Error(
      "SVG previews cannot contain scripts or embedded resources.",
    );
  if (/\son[a-z]+\s*=/i.test(text))
    throw new Error("SVG event handlers are not supported.");
  if (/<style\b[^>]*>[\s\S]*?(?:@import|url\(\s*["']?(?!#))/i.test(text))
    throw new Error("SVG previews cannot load external styles or resources.");
  for (const match of text.matchAll(
    /\b(?:href|xlink:href)\s*=\s*(["'])(.*?)\1/gi,
  ))
    if (!match[2].trim().startsWith("#"))
      throw new Error("SVG previews cannot load external links or resources.");
  for (const match of text.matchAll(/url\(\s*["']?([^)'"\s]+)["']?\s*\)/gi))
    if (!match[1].startsWith("#"))
      throw new Error("SVG previews cannot load external resources.");
}

function length(value: string | null) {
  if (!value) return 0;
  const match = /^\s*([\d.]+)(?:px)?\s*$/i.exec(value);
  return match ? Number(match[1]) : 0;
}

export function svgDimensions(text: string) {
  assertSafeSvgText(text);
  const start = /<svg\b([^>]*)>/i.exec(text)?.[1] ?? "",
    attr = (name: string) =>
      new RegExp(`\\b${name}\\s*=\\s*(["'])(.*?)\\1`, "i").exec(start)?.[2] ??
      null,
    viewBox = (attr("viewBox") || "")
      .trim()
      .split(/[\s,]+/)
      .map(Number),
    w = length(attr("width")) || (viewBox.length === 4 ? viewBox[2] : 0),
    h = length(attr("height")) || (viewBox.length === 4 ? viewBox[3] : 0);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0)
    throw new Error("SVG preview needs a positive width, height or viewBox.");
  if (w * h > 40_000_000)
    throw new Error("Use an image with fewer than 40 million pixels.");
  return { w, h };
}

export const IMAGE_FORMAT_MATRIX = Object.values(formats);
